// Geometria do silo em unidades de cena, a mesma do Silo3dComponent.vue
// (raio por arco, ângulo de cada cabo no arco, comprimento do cabo e telhado).
// Se a geometria do 3D mudar, atualize aqui também.

export const R = 1.7;
export const Y_BASE = -1;
export const CORPO_H = 2;
export const Y_BEIRAL = Y_BASE + CORPO_H;
export const BEIRAL = 0.07;
export const TELHADO_H = 0.72;
export const R_TELHADO = R + BEIRAL;

const RAIO_ARCO_MAP = {
    1: [R * 0.5],
    2: [R * 0.8, R * 0.4],
    3: [R * 0.75, R * 0.5, R * 0.25],
    4: [R * 0.8, R * 0.6, R * 0.4, R * 0.2],
    5: [R * 0.831, R * 0.653, R * 0.499, R * 0.333, R * 0.166],
};
const MARGEM_CABO = 0.12;
const EXTRA_TOPO_CABO = 0.2;
const FOLGA_TELHADO = 0.1;
const ARCO_CENTRAL = 999;

export function roofYAt(raio) {
    return Y_BEIRAL + TELHADO_H * (1 - Math.min(raio, R_TELHADO) / R_TELHADO);
}

function anguloNoArco(cabos, k, n, arco) {
    const doArco = cabos.filter(c => c.arco !== ARCO_CENTRAL);
    if (!doArco.length || !n) return 0;
    const arcos = [...new Set(doArco.map(c => c.arco))].sort((a, b) => a - b);
    const qtd = a => doArco.filter(c => c.arco === a).length;
    let inicio = ((qtd(arcos[0]) - 1) / qtd(arcos[0])) * Math.PI * 2;
    const idx = Math.max(0, arcos.indexOf(arco));
    for (let i = 1; i <= idx; i++) inicio -= Math.PI / qtd(arcos[i - 1]);
    return inicio - (k / n) * Math.PI * 2;
}

function alturasSensores(raio, sensores, central) {
    const botY = Y_BASE;
    const comprimento = central ? 2.5 + EXTRA_TOPO_CABO : 2.47 - raio * 0.5 + EXTRA_TOPO_CABO;
    const y0 = botY + MARGEM_CABO;
    const y1 = Math.max(y0, Math.min(botY + comprimento - MARGEM_CABO, roofYAt(raio) - FOLGA_TELHADO));
    const n = Math.max(1, sensores);
    return Array.from({ length: n }, (_, j) => (n > 1 ? y0 + ((y1 - y0) / (n - 1)) * j : (y0 + y1) / 2));
}

/**
 * Posição 3D (raio, ângulo) e altura de cada sensor, por cabo.
 * `cabos`: [{ cabo, arco, posicao, sensores }] da configuração do silo.
 */
export function geometriaCabos(cabos) {
    const lista = (cabos || []).slice().sort((a, b) => Number(a.cabo) - Number(b.cabo));
    const arcos = [...new Set(lista.map(c => c.arco))].filter(a => a !== ARCO_CENTRAL);
    const nArcos = arcos.length;
    const out = [];
    arcos.forEach((arco, index) => {
        const doArco = lista.filter(c => c.arco === arco).sort((a, b) => (a.posicao ?? 0) - (b.posicao ?? 0));
        const raio = (RAIO_ARCO_MAP[nArcos] && RAIO_ARCO_MAP[nArcos][index]) ?? R * 0.5;
        doArco.forEach((c, k) => {
            out.push({
                cabo: String(c.cabo), arco, raio,
                angulo: anguloNoArco(lista, k, doArco.length, arco),
                alturas: alturasSensores(raio, c.sensores, false),
                topoCabo: roofYAt(raio) - 0.02,
            });
        });
    });
    lista.filter(c => c.arco === ARCO_CENTRAL).forEach(c => {
        out.push({
            cabo: String(c.cabo), arco: ARCO_CENTRAL, raio: 0, angulo: 0,
            alturas: alturasSensores(0, c.sensores, true),
            topoCabo: roofYAt(0) - 0.04,
        });
    });
    return out;
}

// Armazém (mesma geometria do buildWarehouse do Silo3dComponent.vue): galpão de
// duas águas, cabos em fileiras (arcos) ao longo da profundidade e em 9 posições na
// largura; o comprimento do cabo é proporcional ao número de sensores.
const ARMAZEM_ESCALA = 1.7;
export const ARMAZEM = {
    largura: 3.5 * ARMAZEM_ESCALA,
    altura: 0.9 * ARMAZEM_ESCALA,
    yPiso: -1,
    posicoes: 9,
};
ARMAZEM.telhadoAltura = (ARMAZEM.largura / 2) * Math.tan(Math.PI / 6);

export function tetoArmazem(x) {
    const hL = ARMAZEM.largura / 2;
    return ARMAZEM.yPiso + ARMAZEM.altura + ARMAZEM.telhadoAltura * (1 - Math.min(Math.abs(x), hL) / hL);
}

/**
 * Cabos do armazém vistos de frente. Cabos de fileiras diferentes na mesma posição
 * ficam lado a lado dentro do espaço daquela posição, para não se sobreporem.
 */
export function geometriaArmazem(cabos) {
    const { largura, posicoes, yPiso } = ARMAZEM;
    const hL = largura / 2;
    const passo = largura / (posicoes + 1);
    const validos = (cabos || []).filter(c => {
        const p = parseInt(c.posicao, 10);
        return !Number.isNaN(p) && p >= 1 && p <= posicoes;
    });
    const maxSens = Math.max(1, ...validos.map(c => c.sensores || 1));
    const fileiras = [...new Set(validos.map(c => c.arco))].sort((a, b) => a - b);
    const out = [];
    for (let p = 1; p <= posicoes; p++) {
        const naPosicao = validos.filter(c => parseInt(c.posicao, 10) === p)
            .sort((a, b) => fileiras.indexOf(a.arco) - fileiras.indexOf(b.arco));
        const m = naPosicao.length;
        const larguraGrupo = passo * 0.7;
        naPosicao.forEach((c, k) => {
            const x = -hL + p * passo + (m > 1 ? (k / (m - 1) - 0.5) * larguraGrupo : 0);
            const topo = tetoArmazem(x) - 0.05;
            const botY = yPiso + 0.02;
            const sensores = Math.max(1, c.sensores || 0);
            const comprimento = (topo - botY) * (sensores / maxSens);
            const y0 = botY + MARGEM_CABO;
            const y1 = Math.max(y0, botY + comprimento - MARGEM_CABO);
            out.push({
                cabo: String(c.cabo), arco: c.arco, x, topoCabo: topo,
                alturas: Array.from({ length: sensores }, (_, j) => (sensores > 1 ? y0 + ((y1 - y0) / (sensores - 1)) * j : (y0 + y1) / 2)),
            });
        });
    }
    return out;
}
